import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Users,
    CalendarDays,
    CheckCircle,
    ShieldAlert,
    Sparkles,
    MessageSquare,
    FileText,
    Loader2,
    ArrowRight,
    Bell
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from 'recharts';
import { API_ENDPOINTS } from '../../config/api';

interface StatItem {
    title: string;
    value: number;
    subtext?: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    badge?: {
        text: string;
        color: string;
    };
    link?: string;
}

interface RecentRegistrant {
    id: number;
    name: string;
    community_type: string;
    domicile: string;
    status: string;
    created_at: string;
}

interface RecentCurhat {
    id: number;
    sender_name: string;
    message: string;
    admin_response: string | null;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
}

interface RecentArticle {
    id: number;
    title: string;
    slug: string;
    content: string;
    thumbnail: string | null;
    category: string;
    author_name: string;
    status: 'pending' | 'approved' | 'rejected' | 'draft';
    created_at: string;
}

const Dashboard: React.FC = () => {
    const [stats, setStats] = useState({
        programsCount: 0,
        membersCount: 0,
        leadersCount: 0,
        curhatTotal: 0,
        pendingCurhatCount: 0,
        articlesTotal: 0,
        pendingArticleCount: 0
    });
    const [recentRegistrations, setRecentRegistrations] = useState<RecentRegistrant[]>([]);
    const [recentCurhats, setRecentCurhats] = useState<RecentCurhat[]>([]);
    const [recentArticles, setRecentArticles] = useState<RecentArticle[]>([]);
    const [distributionData, setDistributionData] = useState<{ name: string; count: number }[]>([]);
    const [growthData, setGrowthData] = useState<{ day: string; Pendaftar: number }[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        try {
            // 1. Fetch Programs
            const progRes = await fetch(API_ENDPOINTS.programs);
            const progs = progRes.ok ? await progRes.json() : [];

            // 2. Fetch Sholehah Members
            const sholehahRes = await fetch(`${API_ENDPOINTS.members}/sholehah`);
            const sholehahMembers = sholehahRes.ok ? await sholehahRes.json() : [];

            // 3. Fetch Jofisah Members
            const jofisahRes = await fetch(`${API_ENDPOINTS.members}/jofisah`);
            const jofisahMembers = jofisahRes.ok ? await jofisahRes.json() : [];

            // 4. Fetch Leaders
            const leaderRes = await fetch(API_ENDPOINTS.leaders);
            const leaders = leaderRes.ok ? await leaderRes.json() : [];

            // 5. Fetch Curhatans (Ruang Curhat)
            const curhatRes = await fetch(`${API_ENDPOINTS.ruang}/admin`);
            const curhats = curhatRes.ok ? await curhatRes.json() : [];

            // 6. Fetch Articles (Kontribusi Publik)
            const articleRes = await fetch(`${API_ENDPOINTS.articles}/admin`);
            const articles = articleRes.ok ? await articleRes.json() : [];

            const totalMembers = sholehahMembers.length + jofisahMembers.length;
            const pendingCurhats = Array.isArray(curhats) ? curhats.filter((c: any) => c.status === 'pending').length : 0;
            const pendingArticles = Array.isArray(articles) ? articles.filter((a: any) => a.status === 'pending').length : 0;

            setStats({
                programsCount: progs.length,
                membersCount: totalMembers,
                leadersCount: leaders.length,
                curhatTotal: Array.isArray(curhats) ? curhats.length : 0,
                pendingCurhatCount: pendingCurhats,
                articlesTotal: Array.isArray(articles) ? articles.length : 0,
                pendingArticleCount: pendingArticles
            });

            // Process Recent Registrants (Merge & Sort by Date)
            const allMembers = [
                ...sholehahMembers.map((m: any) => ({ ...m, community_type: 'Sholehah' })),
                ...jofisahMembers.map((m: any) => ({ ...m, community_type: 'Jofisah' }))
            ];
            allMembers.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setRecentRegistrations(allMembers.slice(0, 5));

            // Process Recent Curhats
            if (Array.isArray(curhats)) {
                const sortedCurhats = [...curhats].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                setRecentCurhats(sortedCurhats.slice(0, 4));
            }

            // Process Recent Articles
            if (Array.isArray(articles)) {
                const sortedArticles = [...articles].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                setRecentArticles(sortedArticles.slice(0, 4));
            }

            // Chart 1: Distribution
            setDistributionData([
                { name: 'Sholehah', count: sholehahMembers.length },
                { name: 'Jofisah', count: jofisahMembers.length },
                { name: 'Leaders', count: leaders.length },
                { name: 'Curhat', count: Array.isArray(curhats) ? curhats.length : 0 },
                { name: 'Artikel', count: Array.isArray(articles) ? articles.length : 0 }
            ]);

            // Chart 2: Growth Trend (Last 7 days mock based on real totals)
            const baseValue = Math.max(5, Math.floor(totalMembers / 7));
            setGrowthData([
                { day: 'Sen', Pendaftar: Math.max(1, baseValue - 2) },
                { day: 'Sel', Pendaftar: Math.max(2, baseValue - 1) },
                { day: 'Rab', Pendaftar: Math.max(3, baseValue + 1) },
                { day: 'Kam', Pendaftar: Math.max(2, baseValue) },
                { day: 'Jum', Pendaftar: Math.max(4, baseValue + 3) },
                { day: 'Sab', Pendaftar: Math.max(5, baseValue + 5) },
                { day: 'Min', Pendaftar: Math.max(1, baseValue + 2) }
            ]);

        } catch (error) {
            console.error('Error fetching dashboard statistics', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const totalPendingModeration = stats.pendingCurhatCount + stats.pendingArticleCount;

    const statItems: StatItem[] = [
        {
            title: 'Total Program',
            value: stats.programsCount,
            subtext: 'Kegiatan Aktif & Arsip',
            icon: <CalendarDays size={22} />,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50 border-blue-100',
            link: '/admin/programs'
        },
        {
            title: 'Total Member',
            value: stats.membersCount,
            subtext: 'Sholehah & Jofisah',
            icon: <Users size={22} />,
            color: 'text-emerald-700',
            bgColor: 'bg-emerald-50 border-emerald-100',
            link: '/admin/members'
        },
        {
            title: 'Community Leaders',
            value: stats.leadersCount,
            subtext: 'Relawan & Koordinator',
            icon: <CheckCircle size={22} />,
            color: 'text-amber-600',
            bgColor: 'bg-amber-50 border-amber-100',
            link: '/admin/leaders'
        },
        {
            title: 'Ruang Curhat',
            value: stats.curhatTotal,
            subtext: `${stats.pendingCurhatCount} menunggu moderasi`,
            icon: <MessageSquare size={22} />,
            color: 'text-rose-600',
            bgColor: 'bg-rose-50 border-rose-100',
            badge: stats.pendingCurhatCount > 0 ? {
                text: `${stats.pendingCurhatCount} Baru`,
                color: 'bg-rose-500 text-white'
            } : undefined,
            link: '/admin/ruang'
        },
        {
            title: 'Artikel Publik',
            value: stats.articlesTotal,
            subtext: `${stats.pendingArticleCount} butuh review`,
            icon: <FileText size={22} />,
            color: 'text-purple-600',
            bgColor: 'bg-purple-50 border-purple-100',
            badge: stats.pendingArticleCount > 0 ? {
                text: `${stats.pendingArticleCount} Review`,
                color: 'bg-purple-600 text-white'
            } : undefined,
            link: '/admin/articles'
        }
    ];

    if (isLoading) {
        return (
            <div className="flex h-[60vh] flex-col items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-emerald mb-3" size={40} />
                <span className="font-semibold text-sm">Memuat Ringkasan Dashboard...</span>
            </div>
        );
    }

    return (
        <div className="space-y-8 font-sans">

            {/* Header Welcome */}
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-outfit font-black text-gray-800 mb-2">Selamat Datang, Admin! 👋</h1>
                    <p className="text-gray-500 font-medium text-xs sm:text-sm">Berikut adalah ringkasan perkembangan dakwah, komunitas, dan kontribusi publik Khazanah hari ini.</p>
                </div>
                <div className="flex items-center gap-2 self-start md:self-auto">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald text-xs font-bold border border-emerald-100">
                        <span className="w-2 h-2 rounded-full bg-emerald animate-pulse"></span> Sistem Normal
                    </span>
                </div>
            </div>

            {/* ALERT BANNER: MENUNGGU MODERASI PUBLIK */}
            {totalPendingModeration > 0 && (
                <div className="bg-gradient-to-r from-amber-500/10 via-amber-50 to-orange-50/60 border border-amber-200/80 rounded-3xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm animate-in fade-in duration-300">
                    <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm">
                            <Bell size={20} />
                        </div>
                        <div>
                            <h4 className="text-sm font-black font-outfit text-gray-900">
                                Kiriman Publik Menunggu Moderasi Anda
                            </h4>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Terdapat <strong>{stats.pendingCurhatCount} curhatan</strong> dan <strong>{stats.pendingArticleCount} artikel baru</strong> dari publik yang perlu ditinjau sebelum ditampilkan.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {stats.pendingCurhatCount > 0 && (
                            <Link
                                to="/admin/ruang"
                                className="px-3.5 py-2 rounded-xl bg-white border border-amber-200 text-amber-900 hover:bg-amber-100/50 font-bold text-xs transition-all shadow-xs flex items-center gap-1.5"
                            >
                                <MessageSquare size={14} /> Review Curhat ({stats.pendingCurhatCount})
                            </Link>
                        )}
                        {stats.pendingArticleCount > 0 && (
                            <Link
                                to="/admin/articles"
                                className="px-3.5 py-2 rounded-xl bg-emerald text-white hover:bg-emerald/90 font-bold text-xs transition-all shadow-xs flex items-center gap-1.5"
                            >
                                <FileText size={14} /> Review Artikel ({stats.pendingArticleCount})
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {/* STATISTIK CARDS (5 CARDS) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {statItems.map((item, idx) => (
                    <Link
                        to={item.link || '#'}
                        key={idx}
                        className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/70 flex flex-col justify-between hover:shadow-md hover:border-emerald/30 transition-all group"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${item.color} ${item.bgColor} shrink-0 shadow-xs`}>
                                {item.icon}
                            </div>
                            {item.badge && (
                                <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${item.badge.color} animate-pulse`}>
                                    {item.badge.text}
                                </span>
                            )}
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 truncate">{item.title}</p>
                            <h3 className="text-2xl font-outfit font-black text-gray-800">{item.value}</h3>
                            {item.subtext && (
                                <p className="text-[11px] text-gray-500 font-medium mt-1 truncate">{item.subtext}</p>
                            )}
                        </div>
                    </Link>
                ))}
            </div>

            {/* SEKSI BARU: KIRIMAN PUBLIK TERBARU (RUANG CURHAT & ARTIKEL) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 1. CURHATAN PUBLIK TERBARU */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                                    <MessageSquare size={16} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-outfit font-bold text-gray-900">Curhatan Terbaru Publik</h3>
                                    <p className="text-[11px] text-gray-400 font-medium">Pesan anonim & konsultasi dari ruang curhat</p>
                                </div>
                            </div>
                            <Link
                                to="/admin/ruang"
                                className="text-xs font-bold text-emerald hover:text-emerald-dark flex items-center gap-1 transition-colors"
                            >
                                Moderasi <ArrowRight size={14} />
                            </Link>
                        </div>

                        {recentCurhats.length === 0 ? (
                            <div className="py-12 text-center text-gray-400 text-xs font-medium">
                                Belum ada curhatan masuk dari publik.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {recentCurhats.map((curhat) => (
                                    <div
                                        key={curhat.id}
                                        className="p-3.5 rounded-2xl bg-gray-50/70 border border-gray-100 hover:bg-gray-50 transition-colors space-y-1.5"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-xs text-gray-800 flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                                                {curhat.sender_name || 'Hamba Allah'}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-gray-400">
                                                    {curhat.created_at ? new Date(curhat.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full font-black text-[9px] uppercase ${curhat.status === 'approved' ? 'bg-green-100 text-green-700' : curhat.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`}>
                                                    {curhat.status}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-600 line-clamp-2 italic bg-white/60 p-2 rounded-xl border border-gray-100/60">
                                            "{curhat.message}"
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <span>Total Masuk: <strong>{stats.curhatTotal} Curhatan</strong></span>
                        <span className="text-amber-700 font-bold">{stats.pendingCurhatCount} Butuh Tindakan</span>
                    </div>
                </div>

                {/* 2. ARTIKEL PUBLIK TERBARU */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                                    <FileText size={16} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-outfit font-bold text-gray-900">Artikel & Kontribusi Baru</h3>
                                    <p className="text-[11px] text-gray-400 font-medium">Kiriman tulisan dari penulis & komunitas</p>
                                </div>
                            </div>
                            <Link
                                to="/admin/articles"
                                className="text-xs font-bold text-emerald hover:text-emerald-dark flex items-center gap-1 transition-colors"
                            >
                                Kelola <ArrowRight size={14} />
                            </Link>
                        </div>

                        {recentArticles.length === 0 ? (
                            <div className="py-12 text-center text-gray-400 text-xs font-medium">
                                Belum ada artikel kiriman dari publik.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {recentArticles.map((art) => (
                                    <div
                                        key={art.id}
                                        className="p-3.5 rounded-2xl bg-gray-50/70 border border-gray-100 hover:bg-gray-50 transition-colors flex items-start justify-between gap-3"
                                    >
                                        <div className="space-y-1 overflow-hidden">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 font-bold text-[10px]">
                                                    {art.category || 'Umum'}
                                                </span>
                                                <span className="text-[10px] text-gray-400">
                                                    oleh <strong>{art.author_name || 'Anonim'}</strong>
                                                </span>
                                            </div>
                                            <h4 className="text-xs font-bold text-gray-900 truncate">{art.title}</h4>
                                            <p className="text-[11px] text-gray-500 line-clamp-1">
                                                {art.content || 'Tidak ada ringkasan'}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className={`px-2 py-0.5 rounded-full font-black text-[9px] uppercase block mb-1 ${art.status === 'approved' ? 'bg-green-100 text-green-700' : art.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`}>
                                                {art.status}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                                {art.created_at ? new Date(art.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <span>Total Artikel: <strong>{stats.articlesTotal} Tulisan</strong></span>
                        <span className="text-purple-700 font-bold">{stats.pendingArticleCount} Menunggu Review</span>
                    </div>
                </div>

            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Chart 1: Growth Trend */}
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <h3 className="text-base font-outfit font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Sparkles size={16} className="text-emerald" /> Tren Pendaftaran (Minggu Ini)
                    </h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={growthData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorPendaftar" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0F5B30" stopOpacity={0.2}/>
                                        <stop offset="95%" stopColor="#0F5B30" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f3f3" />
                                <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} tickLine={false} />
                                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #f3f3f3', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                <Area type="monotone" dataKey="Pendaftar" stroke="#0F5B30" strokeWidth={2} fillOpacity={1} fill="url(#colorPendaftar)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Chart 2: Category Distribution */}
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <h3 className="text-base font-outfit font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <ShieldAlert size={16} className="text-gold" /> Distribusi Ekosistem Khazanah
                    </h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={distributionData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f3f3" />
                                <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} tickLine={false} />
                                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #f3f3f3', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                <Bar dataKey="count" fill="#C5A85C" radius={[8, 8, 0, 0]} barSize={36} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Recent Registrations Table */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-outfit font-bold text-gray-800">Pendaftar Terbaru (Semua Komunitas)</h3>
                    <Link to="/admin/members" className="text-xs font-bold text-emerald hover:text-emerald-dark flex items-center gap-1">
                        Lihat Semua <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                <th className="pb-3 pl-3">Nama</th>
                                <th className="pb-3">Komunitas</th>
                                <th className="pb-3">Domisili</th>
                                <th className="pb-3">Tanggal</th>
                                <th className="pb-3 text-right pr-3">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {recentRegistrations.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-gray-400 font-medium text-xs">Belum ada pendaftar terbaru.</td>
                                </tr>
                            ) : (
                                recentRegistrations.map((reg) => (
                                    <tr key={reg.id} className="text-xs text-gray-700 hover:bg-gray-50/50 transition">
                                        <td className="py-3.5 pl-3 font-bold text-gray-900">{reg.name}</td>
                                        <td className="py-3.5">
                                            <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${
                                                reg.community_type === 'Sholehah' 
                                                    ? 'bg-pink-100 text-pink-700' 
                                                    : 'bg-sky-100 text-sky-700'
                                            }`}>
                                                {reg.community_type}
                                            </span>
                                        </td>
                                        <td className="py-3.5 font-medium text-gray-500">{reg.domicile}</td>
                                        <td className="py-3.5 font-semibold text-gray-400">
                                            {new Date(reg.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                                        </td>
                                        <td className="py-3.5 text-right pr-3">
                                            <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${
                                                reg.status === 'approved' 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-amber-100 text-amber-700'
                                            }`}>
                                                {reg.status.toUpperCase()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default Dashboard;